/**
 * The Aeven colour specification, and every shade the portal derives from it.
 *
 * This is the only place a brand colour is written down. Earlier theme values
 * were eyedropped from a picture of the brand sheet, which put every one of
 * them a few points off the real thing (`#586051` for Bronze Green, `#eb5c4c`
 * for Energy Red, `#ebe9dd` for Warm Light), while other modules had already
 * started hardcoding the correct hexes inline - so the app shipped two
 * palettes that disagreed. Anything that needs a brand colour imports it from
 * here.
 *
 * The specification has nine colours and no opinion about hover states, raised
 * surfaces or a dark theme, so those are computed from the nine by `mix` below
 * rather than picked by eye. That is the rule for adding one: derive it, name
 * it, say what it is for.
 */

/** The nine specified colours, exactly as published. */
export const brand = {
  bronzeGreen: '#5A6053',
  navy: '#31495B',
  sea: '#7998AE',
  charcoal: '#242424',
  energyRed: '#CD5C4E',
  wood: '#B89F7F',
  sand: '#DCD7C7',
  warmLight: '#E9E8DC',
  pale: '#FAF8F1',
} as const;

const WHITE = '#FFFFFF';
const BLACK = '#000000';

const channel = (hex: string, index: number) =>
  parseInt(hex.slice(1 + index * 2, 3 + index * 2), 16);

/**
 * Linear blend of two hex colours, `ratio` being how much of `towards` ends up
 * in the result. Used to build every shade that is not in the specification,
 * so each one can be read back as "this much of that official colour".
 */
export const mix = (base: string, towards: string, ratio: number) =>
  `#${[0, 1, 2]
    .map(i =>
      Math.round(channel(base, i) * (1 - ratio) + channel(towards, i) * ratio)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')
    .toUpperCase()}`;

/**
 * Shades derived from the nine. Names say where they came from, and the
 * comment says what the portal uses them for.
 */
export const derived = {
  /** Bronze Green 80% + Charcoal - secondary text, sidebar submenu ground. */
  bronzeMuted: mix(brand.bronzeGreen, brand.charcoal, 0.2),
  /** Bronze Green 65% + Charcoal - sidebar hover. */
  bronzeDeep: mix(brand.bronzeGreen, brand.charcoal, 0.35),
  /** Bronze Green 45% + Charcoal - far end of the light header gradient. */
  bronzeShadow: mix(brand.bronzeGreen, brand.charcoal, 0.55),
  /** Charcoal + 10% Bronze Green - raised surface in the dark theme. */
  charcoalRaised: mix(brand.charcoal, brand.bronzeGreen, 0.1),
  /** Charcoal + 30% Bronze Green - divider in the dark theme. */
  charcoalBorder: mix(brand.charcoal, brand.bronzeGreen, 0.3),
  /** Charcoal darkened - dark sidebar, which has to sit under the page. */
  charcoalSunk: mix(brand.charcoal, BLACK, 0.35),
  /** Energy Red 65% + Charcoal - Energy Red is too light to carry text. */
  redDeep: mix(brand.energyRed, brand.charcoal, 0.35),
  /** Wood 60% + Charcoal - same problem, same fix, for the warning state. */
  woodDeep: mix(brand.wood, brand.charcoal, 0.4),
  /** Bronze Green 55% + Pale - Bronze Green on Charcoal is only 2.39:1. */
  bronzeLight: mix(brand.bronzeGreen, brand.pale, 0.45),
  /** Energy Red 70% + Pale - the dark theme's error ink. */
  redLight: mix(brand.energyRed, brand.pale, 0.3),
  /** Halfway between Navy and Sea - one more blue, for the Azure family. */
  navySteel: mix(brand.navy, brand.sea, 0.5),
} as const;

/**
 * Where each specified colour is allowed to appear.
 *
 * Three of the nine cannot carry text on a light surface at all: Sea reaches
 * 2.86:1 on Pale, Wood 2.38:1 and Energy Red 3.77:1, against the 4.5:1 that
 * WCAG AA asks for. They are fills, category marks and accents; when the same
 * hue has to carry a label, the theme reaches for `derived.redDeep` or
 * `derived.woodDeep` instead. `contrast.test.ts` holds every pairing the
 * themes below actually produce to the AA line, so this stays true.
 */
export const semantic = {
  /** Structure: sidebar, buttons, the light header wave, "healthy". */
  primary: brand.bronzeGreen,
  /** Links on a light surface - the strongest reading colour in the set. */
  link: brand.navy,
  /** Links on a dark surface, where Navy and Bronze Green both go muddy. */
  linkDark: brand.sea,
  /** Accent. Indicators, one call to action per view. Never body text. */
  accent: brand.energyRed,
} as const;

/**
 * The stack status ramp: background and the foreground that goes on it.
 *
 * Kept as pairs because the specification's lighter colours need Charcoal on
 * top and its darker ones need Pale, and every previous version of this got it
 * wrong by putting white on everything.
 */
export const statusColours = {
  ok: { bg: brand.bronzeGreen, fg: brand.pale },
  busy: { bg: brand.wood, fg: brand.charcoal },
  /** Slate, not grey: a stopped machine is still there and still costs quota. */
  stopped: { bg: brand.sea, fg: brand.charcoal },
  failed: { bg: derived.redDeep, fg: brand.pale },
  gone: { bg: brand.sand, fg: brand.charcoal },
} as const;

/** Light theme surfaces and inks. */
export const light = {
  primary: semantic.primary,
  link: semantic.link,
  bg: brand.pale,
  surface: WHITE,
  /** Raised rows and table headers - Warm Light is the step between the two. */
  surfaceAlt: brand.warmLight,
  border: brand.sand,
  text: brand.charcoal,
  textMuted: derived.bronzeMuted,
  navBg: semantic.primary,
  navColor: brand.sand,
  navSelected: brand.pale,
  navHover: derived.bronzeDeep,
  navSubmenu: derived.bronzeMuted,
} as const;

/** Dark theme surfaces and inks. */
export const dark = {
  primary: semantic.linkDark,
  link: semantic.linkDark,
  bg: brand.charcoal,
  surface: derived.charcoalRaised,
  surfaceAlt: derived.charcoalBorder,
  border: derived.charcoalBorder,
  text: brand.pale,
  textMuted: brand.sand,
  navBg: derived.charcoalSunk,
  navColor: brand.sand,
  navSelected: brand.pale,
  navHover: derived.charcoalRaised,
  navSubmenu: brand.charcoal,
} as const;

/**
 * State colours. The specification has no green or amber, so rather than
 * introduce colours from outside it, success reuses Bronze Green, information
 * reuses Navy, and the two states that need a warmer hue take the darkened
 * Energy Red and Wood - dark enough to be read as text on Pale.
 */
export const stateLight = {
  success: brand.bronzeGreen,
  warning: derived.woodDeep,
  error: derived.redDeep,
  info: brand.navy,
} as const;

/**
 * The same four states for the dark theme. They cannot be shared: the light
 * set is chosen to be dark enough for Pale, which leaves Bronze Green at
 * 2.39:1 on Charcoal. Each one is the same brand hue moved the other way.
 */
export const stateDark = {
  success: derived.bronzeLight,
  warning: brand.wood,
  error: derived.redLight,
  info: brand.sea,
} as const;

/** Relative luminance, WCAG 2.1 definition. */
const luminance = (hex: string) => {
  const [r, g, b] = [0, 1, 2]
    .map(i => channel(hex, i) / 255)
    .map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Contrast ratio between two hex colours. Exported so the theme's own tests
 * can hold every pairing to the WCAG AA line instead of trusting a reviewer's
 * eye - AA is 4.5:1 for body text and 3:1 for large text and UI shapes.
 */
export const contrastRatio = (a: string, b: string) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * Which of the two ends of the palette to put on a given background.
 *
 * The specification runs from Charcoal to Pale, so anything drawn on a brand
 * colour - a monogram tile, a status chip - wants one or the other rather than
 * white on everything, which is what put a 2.53:1 glyph on Wood.
 */
export const onColour = (background: string) =>
  contrastRatio(brand.charcoal, background) >=
  contrastRatio(brand.pale, background)
    ? brand.charcoal
    : brand.pale;
